import React from 'react'
import { FormattedMessage } from 'react-intl'
import { proxy, useSnapshot } from 'valtio'

import { TtButton, TtIcon } from '@23/react'

import isHeadless from '../headless/isHeadless'
import { reloadRoom } from '../headless/src/reload'
import { logMessage } from '../Logging/log'

import { type FatalErrorKey, fatalErrorMessages } from './FatalErrorMessages'

import './FatalError.scss'

type FatalErrorStore = {
  message: string | null
  reload: 'no' | 'yes' | 'ask'
  reason: unknown
}

const fatalErrors = proxy<FatalErrorStore>({
  message: null,
  reload: 'no',
  reason: null,
})

const fatalDisconnectCallbacks: (() => void)[] = []

const FatalErrorScreen = () => {
  const { message, reload } = useSnapshot(fatalErrors)

  return (
    <div className="fatal-error-screen">
      <div className="fatal-error-alert">
        <div className="fatal-error-header">
          <FormattedMessage defaultMessage="Error" />
        </div>
        <TtIcon className="fatal-error-icon" library="one" name="page-error" />
        <div className="fatal-error-message">{message}</div>
        {reload === 'ask' && (
          <TtButton
            color="secondary"
            variant="light"
            pill
            className="fatal-error-button"
            onClick={() => window.location.reload()}
          >
            <FormattedMessage defaultMessage="Reload" />
          </TtButton>
        )}
      </div>
    </div>
  )
}

const setFatalError = (
  key: FatalErrorKey,
  { reload = 'no', reason }: Partial<Exclude<FatalErrorStore, 'message'>> = {}
) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (fatalErrors.message || !fatalErrorMessages[key]) return

  const { message, log } = fatalErrorMessages[key]

  if (log) {
    if (typeof log === 'string') {
      logMessage(log, { level: 'fatal', extra: { reason, reload } })
    } else {
      logMessage(log.message, { level: log.level, extra: { reason, reload } })
    }
  }

  if (isHeadless || reload === 'yes') {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    reloadRoom(`reloading after fatal error — ${reason}`)
  }
  fatalErrors.message = message()
  fatalErrors.reload = reload

  void runFatalDisconnects()
}

const runFatalDisconnects = async () => {
  fatalDisconnectCallbacks
    .splice(0, fatalDisconnectCallbacks.length)
    .forEach((callback) => {
      callback()
    })
}

const registerFatalDisconnect = (callback: () => void) => {
  fatalDisconnectCallbacks.push(callback)
  if (fatalErrors.message) {
    void runFatalDisconnects()
  }
}

const useHasFatalError = () => {
  return useSnapshot(fatalErrors).message !== null
}

export {
  FatalErrorScreen,
  setFatalError,
  useHasFatalError,
  registerFatalDisconnect,
  runFatalDisconnects,
}
